import React, { useEffect, useMemo, useState } from 'react';
import * as Bixby from '$/core';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AppState } from '$/store';
import NoDataFound from '$/components/global/noData';
import CreateClientCompanyTask from '$/pages/admin/company/task/CreateClientCompanyTask';
import { getMostRecentCompanyTask, setCurrentCompany } from '$/store/company/actions';
import { toastSuccess } from '$/store/toast/actions';
import './index.scss';
import './CompanyAccess.scss';
import { getIsCurrentUserDocHostingAdminState } from '$/store/profile/selectors';
import dayjs from 'dayjs';
import Loading from '$/components/loading/Loading';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

interface IClientCompanyAccessProps {
  clients: Bixby.Models.CompanyClient[];
  onGridReady: (params: any) => void;
  getContextMenuItems: () => any;
  emailTaskCellRenderer: (params: ICellRendererParams) => JSX.Element;
  missingEmailTaskCellRenderer: (params: ICellRendererParams) => JSX.Element;
  roguePostTrackCellRenderer: (params: ICellRendererParams) => JSX.Element;
  handleCloseCreateTaskModal: () => void;
  isCreatingEmailTask: boolean;
  isCreatingRogueTask: boolean;
  isCreatingMissingEmailTask: boolean;
  currentClient: string | undefined;
  mostRecentCompanyTask?: Bixby.Models.ClientCompanyTask;
  mostRecentCompanyTaskError?: Bixby.Models.BixbyError;
  isLoadingMostRecentCompanyTask?: boolean;
}

const ClientCompanyAccess: React.FC<IClientCompanyAccessProps> = ({
  clients,
  onGridReady,
  getContextMenuItems,
  emailTaskCellRenderer,
  missingEmailTaskCellRenderer,
  roguePostTrackCellRenderer,
  handleCloseCreateTaskModal,
  isCreatingEmailTask,
  isCreatingMissingEmailTask,
  isCreatingRogueTask,
  currentClient,
  mostRecentCompanyTask,
  mostRecentCompanyTaskError,
  isLoadingMostRecentCompanyTask,
}) => {
  const getClientCompanyTaskType = () => {
    if (isCreatingEmailTask) {
      return Bixby.Enums.ClientCompanyTaskType.Email;
    }

    if (isCreatingMissingEmailTask) {
      return Bixby.Enums.ClientCompanyTaskType.MissingEmail;
    }

    if (isCreatingRogueTask) {
      return Bixby.Enums.ClientCompanyTaskType.Rogue;
    }

    return undefined;
  };

  return (
    <div className="company-client-access admin-profile-section bottom">
      <h3>Client Access (Total: {!Bixby.Utils.isNullOrEmpty(clients) ? clients.length : 0})</h3>
      {clients && !Bixby.Utils.isNullOrEmpty(clients) ? (
        <div>
          {isLoadingMostRecentCompanyTask && <Loading inline />}
          {!isLoadingMostRecentCompanyTask && mostRecentCompanyTask && (
            <p>
              Latest task assigned on{' '}
              <span className="bold">{dayjs.utc(mostRecentCompanyTask.dateAssigned).local().format('M/D/YYYY')}</span>{' '}
              at <span className="bold">{dayjs.utc(mostRecentCompanyTask.dateAssigned).local().format('h:mmA')}</span> (
              <span className="bold">{mostRecentCompanyTask.taskType}</span>) by{' '}
              <span className="bold">{mostRecentCompanyTask.createdBy}</span>{' '}
              {mostRecentCompanyTask.assignedTo ? 'to ' : ' '}
              <span className="bold">{mostRecentCompanyTask.assignedTo}</span> for{' '}
              <span className="bold">{mostRecentCompanyTask.clientName}</span>
            </p>
          )}
          {mostRecentCompanyTaskError && <p className="error">{mostRecentCompanyTaskError.message}</p>}
          <div className="company-access-data-grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 150,
                    resizable: true,
                    lockPinned: true,
                    cellClass: 'align-left',
                  }}
                  onGridReady={onGridReady}
                  rowData={clients as any[]}
                  suppressDragLeaveHidesColumns={true}
                  suppressClipboardPaste={true}
                  suppressMovableColumns={true}
                  getContextMenuItems={getContextMenuItems}
                  animateRows={true}
                  headerHeight={35}
                  rowHeight={35}
                  components={{
                    emailTaskCellRenderer,
                    missingEmailTaskCellRenderer,
                    roguePostTrackCellRenderer,
                  }}
                  columnDefs={[
                    { field: 'client_Name', headerName: 'Client Name', cellClass: 'align-left' },
                    { field: 'subscription_Name', headerName: 'Subscription', cellClass: 'align-left' },
                    { field: 'dataSiteProvider_Email', headerName: 'Data Site Email', cellClass: 'align-left' },
                    {
                      headerName: 'Create Email Task',
                      cellRenderer: 'emailTaskCellRenderer',
                      cellClass: 'align-center',
                      suppressMenu: true,
                    },
                    {
                      headerName: 'Create Missing Email Task',
                      cellRenderer: 'missingEmailTaskCellRenderer',
                      cellClass: 'align-center',
                      suppressMenu: true,
                    },
                    {
                      headerName: 'Create Rogue Post Task',
                      cellRenderer: 'roguePostTrackCellRenderer',
                      cellClass: 'align-center',
                      suppressMenu: true,
                    },
                  ]}></AgGridReact>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <NoDataFound dataType="clients" />
      )}
      <CreateClientCompanyTask
        onCloseModal={handleCloseCreateTaskModal}
        clientCompanyTaskType={getClientCompanyTaskType()}
        currentClient={currentClient}
      />
    </div>
  );
};

export interface ICompanyAccessProps {
  setCurrentCompany: (companyId: string) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
  currentCompany?: Bixby.Models.AdminCompany;
  isEditing?: boolean;
  featureFlags?: string[];
  isDocHostingAdmin?: boolean;
  mostRecentCompanyTask?: Bixby.Models.ClientCompanyTask;
  dispatchGetMostRecentCompanyTask: (companyId: string) => Promise<void>;
  mostRecentCompanyTaskError?: Bixby.Models.BixbyError;
  isLoadingMostRecentCompanyTask?: boolean;
}

const CompanyAccess: React.FC<ICompanyAccessProps> = props => {
  const {
    currentCompany,
    isEditing,
    mostRecentCompanyTask,
    dispatchGetMostRecentCompanyTask,
    mostRecentCompanyTaskError,
    isLoadingMostRecentCompanyTask,
  } = props;
  const { clients } = currentCompany || ({} as Bixby.Models.AdminCompany);
  const { restrictNewsBackdate } = currentCompany || ({} as Bixby.Models.AdminCompany);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isCreatingEmailTask, setIsCreatingEmailTask] = useState(false);
  const [isCreatingMissingEmailTask, setIsCreatingMissingEmailTask] = useState(false);
  const [isCreatingRogueTask, setIsCreatingRogueTask] = useState(false);
  const [currentClient, setCurrentClient] = useState<undefined | string>(undefined);

  useEffect(() => {
    if (currentCompany?.id) {
      dispatchGetMostRecentCompanyTask(currentCompany.id);
    }
  }, [currentCompany?.id, dispatchGetMostRecentCompanyTask]);

  const handleOpenCreateEmailTaskModal = (ev: React.MouseEvent<HTMLButtonElement>, clientName: string) => {
    ev.preventDefault();
    setIsCreatingEmailTask(true);
    setCurrentClient(clientName);
  };

  const handleOpenCreateMissingEmailTaskModal = (ev: React.MouseEvent<HTMLButtonElement>, clientName: string) => {
    ev.preventDefault();
    setIsCreatingMissingEmailTask(true);
    setCurrentClient(clientName);
  };

  const handleOpenCreateRogueTaskModal = (ev: React.MouseEvent<HTMLButtonElement>, clientName: string) => {
    ev.preventDefault();
    setIsCreatingRogueTask(true);
    setCurrentClient(clientName);
  };

  const handleCloseCreateTaskModal = () => {
    setIsCreatingEmailTask(false);
    setIsCreatingRogueTask(false);
    setIsCreatingMissingEmailTask(false);
  };

  const orderedClients: Bixby.Models.CompanyClient[] = useMemo(() => {
    if (!clients) return [];

    return Bixby.Utils.orderBy(clients, _ => _.client_Name);
  }, [clients]);

  const emailTaskCellRenderer = (params: ICellRendererParams) => {
    if (params.data.basisOfAccess !== Bixby.Enums.BasisOfAccess.CloHoldingsAccess) {
      return (
        <button
          className="bix_grid_button"
          disabled={restrictNewsBackdate}
          type="button"
          onClick={e => handleOpenCreateEmailTaskModal(e, params.data.client_Name)}>
          {params.data.client_Name}
        </button>
      );
    }
    return <></>;
  };

  const missingEmailTaskCellRenderer = (params: ICellRendererParams) => {
    if (params.data.basisOfAccess !== Bixby.Enums.BasisOfAccess.CloHoldingsAccess) {
      return (
        <button
          className="bix_grid_button"
          type="button"
          onClick={e => handleOpenCreateMissingEmailTaskModal(e, params.data.client_Name)}>
          {params.data.client_Name}
        </button>
      );
    }
    return <></>;
  };

  const roguePostTrackCellRenderer = (params: ICellRendererParams) => {
    if (params.data.basisOfAccess !== Bixby.Enums.BasisOfAccess.CloHoldingsAccess) {
      return (
        <button
          className="bix_grid_button"
          type="button"
          onClick={e => handleOpenCreateRogueTaskModal(e, params.data.client_Name)}>
          {params.data.client_Name}
        </button>
      );
    }
    return <></>;
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  return isEditing ? (
    <div className="company-client-access admin-profile-section bottom">
      <h3>Client access not available while editing</h3>
    </div>
  ) : (
    <div>
      <Tabs>
        <TabList>
          <Tab>Data Site Permissioned</Tab>
          <Tab>CLO Permissioned</Tab>
        </TabList>
        <TabPanel>
          <ClientCompanyAccess
            clients={orderedClients.filter(c => c.basisOfAccess !== Bixby.Enums.BasisOfAccess.CloHoldingsAccess)}
            onGridReady={onGridReady}
            getContextMenuItems={getContextMenuItems}
            emailTaskCellRenderer={emailTaskCellRenderer}
            missingEmailTaskCellRenderer={missingEmailTaskCellRenderer}
            roguePostTrackCellRenderer={roguePostTrackCellRenderer}
            handleCloseCreateTaskModal={handleCloseCreateTaskModal}
            isCreatingEmailTask={isCreatingEmailTask}
            isCreatingMissingEmailTask={isCreatingMissingEmailTask}
            isCreatingRogueTask={isCreatingRogueTask}
            currentClient={currentClient}
            mostRecentCompanyTask={mostRecentCompanyTask}
            mostRecentCompanyTaskError={mostRecentCompanyTaskError}
            isLoadingMostRecentCompanyTask={isLoadingMostRecentCompanyTask}
          />
        </TabPanel>
        <TabPanel>
          <ClientCompanyAccess
            clients={orderedClients.filter(c => c.basisOfAccess === Bixby.Enums.BasisOfAccess.CloHoldingsAccess)}
            onGridReady={onGridReady}
            getContextMenuItems={getContextMenuItems}
            emailTaskCellRenderer={emailTaskCellRenderer}
            missingEmailTaskCellRenderer={missingEmailTaskCellRenderer}
            roguePostTrackCellRenderer={roguePostTrackCellRenderer}
            handleCloseCreateTaskModal={handleCloseCreateTaskModal}
            isCreatingEmailTask={isCreatingEmailTask}
            isCreatingMissingEmailTask={isCreatingMissingEmailTask}
            isCreatingRogueTask={isCreatingRogueTask}
            currentClient={currentClient}
            mostRecentCompanyTask={mostRecentCompanyTask}
            mostRecentCompanyTaskError={mostRecentCompanyTaskError}
            isLoadingMostRecentCompanyTask={isLoadingMostRecentCompanyTask}
          />
        </TabPanel>
      </Tabs>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  const { isDocHostingAdmin } = getIsCurrentUserDocHostingAdminState(state);

  return {
    currentCompany: state.companyState.currentCompany,
    mostRecentCompanyTask: state.companyState.mostRecentCompanyTask,
    mostRecentCompanyTaskError: state.companyState.mostRecentCompanyTaskError,
    isLoadingMostRecentCompanyTask: state.companyState.isLoadingMostRecentCompanyTask,
    isEditing: state.draftState.isEditing,
    featureFlags: state.loginState.featureFlags,
    isDocHostingAdmin,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  setCurrentCompany: (companyId: string) => dispatch(setCurrentCompany(companyId)),
  dispatchGetMostRecentCompanyTask: (companyId: string) => dispatch(getMostRecentCompanyTask(companyId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyAccess);
